import { useState, useEffect, useRef } from "react";
import { Box, HStack, Spinner, Text } from "@chakra-ui/react";
import { ReviewCard } from "./ReviewCard.jsx";

const PAGE_SIZE = 20;

export function ReviewGrid({ reviews = [], onFullyLoaded }) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  const visibleReviews = reviews.slice(0, visibleCount);
  const hasMore = visibleCount < reviews.length;

  useEffect(() => {
    if (!hasMore && onFullyLoaded) onFullyLoaded();
  }, [hasMore, onFullyLoaded]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, reviews.length));
        }
      },
      { rootMargin: "400px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, reviews.length]);

  // Deal reviews round-robin into two columns for row ordering
  const left = [];
  const right = [];
  visibleReviews.forEach((review, i) => {
    (i % 2 === 0 ? left : right).push(review);
  });

  return (
    <>
      {reviews.length === 0 ? (
        <Text color="muted" textAlign="center" py={12}>
          No reviews yet.
        </Text>
      ) : (
        <HStack
          align="start"
          spacing={4}
          display={{ base: "none", md: "flex" }}
        >
          <Box flex="1">
            {left.map((review, i) => (
              <ReviewCard
                key={review.id}
                review={review}
                variant="full"
                eager={i < 3}
              />
            ))}
          </Box>
          <Box flex="1">
            {right.map((review, i) => (
              <ReviewCard
                key={review.id}
                review={review}
                variant="full"
                eager={i < 3}
              />
            ))}
          </Box>
        </HStack>
      )}
      {/* Single column on mobile */}
      {visibleReviews.length > 0 && (
        <Box display={{ base: "block", md: "none" }}>
          {visibleReviews.map((review, i) => (
            <ReviewCard
              key={review.id}
              review={review}
              variant="full"
              eager={i < 3}
            />
          ))}
        </Box>
      )}
      {/* Sentinel for infinite scroll */}
      {hasMore && (
        <Box ref={sentinelRef} display="flex" justifyContent="center" py={8}>
          <Spinner size="sm" color="accent" />
        </Box>
      )}
    </>
  );
}
